// ============================================================================
// formFieldKind.ts — ДЕТЕРМИНИРОВАННЫЙ классификатор поля АВТО-формы (SRP).
//
// renderField в AutoObjectForm — монолит с ~12 вложенными ветками (anyref/
// assignee/address/reference/source/...). Здесь — ЧИСТАЯ функция: по типу поля
// и контексту возвращает «вид» поля (какой рендерер/компонент использовать).
// Компонент остаётся тонким маршрутизатором, без if-цепочки в теле рендера.
// ============================================================================

export type FieldKind =
  | 'anyref'      // «Любая ссылка» (пикер тип→значение)
  | 'assignee'    // «Исполнитель» задачи (entities, специализированный выбор)
  | 'address'     // реквизит-ссылка на «Адреса» → Дополнение адреса (AddressField)
  | 'reference'   // обычная ссылка (uuid/reference + refTable) → ReferencePickerField
  | 'source'      // «поле с источником» (source_settings → SourceField)
  | 'default';    // примитив/перечисление → FormFieldRenderer

export interface FieldKindInput {
  /** физический тип поля (dataType/data_type, нижний регистр) */
  dataType: string;
  /** таблица-источник ссылки (refTable/ref_table/refCatalogCode) */
  refTable?: string;
  /** признак «Дополнение адреса» уже определён для поля (extensions/default_for) */
  isAddress?: boolean;
  /** task-тип + поле assignee */
  isAssignee?: boolean;
  /** сырое source_settings (JSON-строка) */
  sourceSettings?: string;
}

/** API источника → ЕДИНЫЙ ГЕО-ШОВ. 'geo' — новый, 'dadata' — АЛИАС (dual-read:
 *  существующие формы с api='dadata' продолжают работать), '' — не задан. */
export function isGeoSourceApi(api: unknown): boolean {
  if (api === undefined || api === null) return true;
  const a = String(api).trim().toLowerCase();
  return a === '' || a === 'geo' || a === 'dadata';
}

/** Классификация поля → какой компонент рендерить. ПОРЯДОК ВАЖЕН (как в renderField):
 *  anyref → assignee → address (ссылка на «Адреса» с дополнением) → reference →
 *  source (с валидным operation и гео-совместимым api) → default. */
export function classifyFieldKind(input: FieldKindInput): FieldKind {
  const dt = (input.dataType || 'text').toLowerCase();

  if (dt === 'anyref') return 'anyref';
  if (input.isAssignee) return 'assignee';
  if ((dt === 'uuid' || dt === 'reference') && input.refTable) {
    if (input.isAddress) return 'address';
    return 'reference';
  }
  const sourceRaw = (input.sourceSettings || '').trim();
  if (sourceRaw) {
    try {
      const source = JSON.parse(sourceRaw);
      if (source && source.operation && isGeoSourceApi(source.api)) return 'source';
    } catch {
      // битый source_settings → default (FormFieldRenderer)
    }
  }
  return 'default';
}
